// ===================================================
// AppScreens.js — 앱 화면 렌더링 담당 컴포넌트
//
// 역할:
//   - App.js에서 모든 비즈니스 로직이 완료된 후,
//     어떤 화면을 그릴지 결정하고 렌더링합니다.
//   - 조건별 분기 화면 (로딩, 인증, 잠금) 처리
//   - 메인 레이아웃 (사이드바, 네비, 각 화면 컴포넌트) 렌더링
//
// 로직은 없고 "무엇을 보여줄지"만 담당합니다.
//
// 코드 스플리팅 전략:
//   - 앱 첫 화면(랜딩)에 필요한 컴포넌트만 즉시 로드
//   - 나머지 화면은 lazy()로 지연 로드 → 초기 번들 파싱량 감소
//   - WebProcess 메모리 압박 완화 목적
// ===================================================

import { lazy, Suspense, useState, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { Analytics } from '@vercel/analytics/react';
import { buildAllSessionContexts } from './utils/contextMatcher';
import { useSwipeBack } from './hooks/useSwipeBack';
import Toast from './Toast';
import OfflineBanner from './OfflineBanner';

// ── 즉시 로드: 앱 시작 시 첫 화면에 바로 필요한 컴포넌트 ──
import Sidebar from './Sidebar';
import MobileNav from './MobileNav';
import LandingScreen from './LandingScreen';
import MobileTopBar from './MobileTopBar';

// ── 지연 로드: 유저가 해당 화면으로 이동할 때 처음 로드됨 ──
// 한 번 로드된 이후에는 캐시되어 즉시 렌더링됩니다.
const OnboardingModal   = lazy(() => import('./OnboardingModal'));
const AuthScreen        = lazy(() => import('./AuthScreen'));
const MobileRecordsPage = lazy(() => import('./MobileRecordsPage'));
const QAScreen          = lazy(() => import('./QAScreen'));
const SessionView       = lazy(() => import('./SessionView'));
const KeywordsView      = lazy(() => import('./KeywordsView'));
const KeywordSearchView = lazy(() => import('./KeywordSearchView'));
const SearchView        = lazy(() => import('./SearchView'));
const ChatView          = lazy(() => import('./ChatView'));
const RecordsPage       = lazy(() => import('./RecordsPage'));
const SettingsView      = lazy(() => import('./SettingsView'));
const AdminView         = lazy(() => import('./AdminView'));
const PushSettingsView  = lazy(() => import('./PushSettingsView'));

// 화면별 모바일 GNB 타이틀 반환
const MODE_LABELS = { diary: '다이어리', note: '노트', planner: '플래너', decision: '의사결정' };
function getPageTitle(screen, { selectedKeyword, keywordsMode }) {
  switch (screen) {
    case 'chat':           return '나와의 대화';
    case 'calendar':       return '캘린더';
    case 'settings':       return '설정';
    case 'search':         return '검색';
    case 'keywords':       return keywordsMode ? `${MODE_LABELS[keywordsMode] || keywordsMode} 키워드` : '키워드';
    case 'keyword-search': return selectedKeyword ? `#${selectedKeyword}` : '키워드';
    case 'admin':          return '어드민';
    case 'push-settings':  return '알림 관리';
    default:               return '';
  }
}

export function AppScreens({
  // 인증 상태
  authLoading, passwordRecovery, setPasswordRecovery,
  user, showAuth, setShowAuth,
  // UI 상태
  mode, isOffline,
  guestBannerDismissed, setGuestBannerDismissed,
  sessions, sessionsError, toast, setToast,
  // 화면 전환 상태
  screen, keywordsMode, selectedKeyword, selectedSession,
  mobileDrawerOpen, closeMobileDrawer,
  loading, error, sessionRounds, currentQuestions, questionsLoading,
  hasUnreadChat,
  // 화면 전환 핸들러
  goBack,
  setScreen, setMode,
  handleSelectSession, handleSelectKeywords,
  handleOpenSearch, handleOpenSettings, handleOpenCalendar, handleOpenPushSettings,
  handleOpenChatWithReadMark, handleHome,
  handleKeywordClick, handleBackFromKeywordSearch,
  // AI / 세션 핸들러
  handleLandingNext, handleContinue, handleEnd, handleRegenerateQuestion, handleReportQuestion,
  handleFreewriteSave,
  handleUpdateSession, handleChatSend,
  handleDeleteSession, refreshSessions,
  handleDeleteAll, handleLogout, handleDeleteAccount,
  handleTogglePinned,
  handleReportGuestBlock,
  // 구글 캘린더 연동
  googleCalendar,
  showCalendarPrompt, setShowCalendarPrompt,
  quickQuestion, setQuickQuestion,
  handleQuickQuestionSave,
  showOnboarding, onOnboardingDone,
  // 마스터 어드민
  isMaster, handleOpenAdmin,
}) {
  // 화면 나가기 애니메이션 중 여부
  const [isExiting, setIsExiting] = useState(false);

  // 나가기 애니메이션 재생 후 실제 뒤로가기
  const goBackWithAnimation = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      setIsExiting(false);
      goBack();
    }, 200);
  }, [goBack]);

  // 스와이프로 뒤로가기: qa/landing 제외 + 애니메이션 포함
  useSwipeBack({ onBack: goBackWithAnimation, enabled: screen !== 'landing' && screen !== 'qa' });

  // 인증 확인 중 → 스피너 표시 (흰 화면 대신 로딩 표시)
  if (authLoading) return (
    <div className="auth-loading-screen">
      <div className="ptr-spinner" style={{ width: 24, height: 24, borderWidth: 2.5 }} />
    </div>
  );

  // 비밀번호 재설정 이메일 링크로 돌아온 경우 → 앱 레이아웃 없이 전체 화면
  if (passwordRecovery) {
    return (
      <Suspense fallback={null}>
        <AuthScreen
          isRecovery={true}
          onRecoveryComplete={() => setPasswordRecovery(false)}
        />
      </Suspense>
    );
  }

  return (
    <div className={`app-layout ${mode}`}>
      {/* Vercel Analytics: 웹 환경에서만 렌더링 */}
      {!Capacitor.isNativePlatform() && <Analytics />}

      {/* 오프라인 배너 */}
      {isOffline && <OfflineBanner />}

      {/* 게스트 로그인 권유 배너: 기록 3개 이상이면 표시 */}
      {!user && !guestBannerDismissed && Object.values(sessions).flat().length >= 3 && (
        <div className="guest-login-banner">
          <span>💾 데이터를 안전하게 보관하려면 로그인하세요</span>
          <button className="guest-login-banner-btn" onClick={() => setShowAuth(true)}>로그인</button>
          <button className="guest-login-banner-close" onClick={() => setGuestBannerDismissed(true)}>✕</button>
        </div>
      )}

      {/* 온보딩 모달 — 첫 방문자에게만 1회 표시 */}
      {showOnboarding && (
        <Suspense fallback={null}>
          <OnboardingModal onDone={onOnboardingDone} />
        </Suspense>
      )}

      {/* 토스트 메시지 */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onDone={() => setToast(null)}
          onNavigate={toast.onNavigate || null}
        />
      )}

      {/* 모바일 기록 목록 페이지 */}
      {mobileDrawerOpen && (
        <Suspense fallback={null}>
          <MobileRecordsPage
            sessions={sessions}
            selectedSession={selectedSession}
            screen={screen}
            keywordsMode={keywordsMode}
            onSelectSession={handleSelectSession}
            onSelectKeywords={handleSelectKeywords}
            onClose={closeMobileDrawer}
            onDeleteSession={handleDeleteSession}
            onTogglePinned={handleTogglePinned}
            onRefresh={refreshSessions}
          />
        </Suspense>
      )}

      {/* 좌측 사이드바 (데스크톱) — 스크린 콘텐츠 래퍼 바깥에 위치 */}
      <Sidebar
        sessions={sessions}
        sessionsError={sessionsError}
        onRetryLoad={refreshSessions}
        selectedSession={selectedSession}
        onSelectSession={handleSelectSession}
        onSelectKeywords={handleSelectKeywords}
        onOpenSearch={handleOpenSearch}
        onHome={handleHome}
        onOpenSettings={handleOpenSettings}
        onOpenChat={handleOpenChatWithReadMark}
        onOpenCalendar={handleOpenCalendar}
        mode={mode}
        screen={screen}
        keywordsMode={keywordsMode}
        hasUnreadChat={hasUnreadChat}
      />

      {/* 모바일 하단 네비게이션 바 — 기록, +FAB, 나와의 대화 3구성 */}
      <MobileNav
        mode={mode}
        screen={screen}
        onCreateRecord={() => setScreen('landing')}
        onOpenCalendar={handleOpenCalendar}
        onOpenChat={handleOpenChatWithReadMark}
        hasUnreadChat={hasUnreadChat}
      />

      {/* 모바일 전용 상단 GNB */}
      <MobileTopBar
        screen={screen}
        showAuth={!user && showAuth}
        onOpenSearch={handleOpenSearch}
        onOpenSettings={handleOpenSettings}
        pageTitle={getPageTitle(screen, { selectedKeyword, keywordsMode })}
      />

      {/* 로그인/회원가입 오버레이 — FNB·GNB 위에 표시되지 않도록 z-index 150 */}
      {!user && showAuth && (
        <Suspense fallback={null}>
          <AuthScreen onBack={() => setShowAuth(false)} />
        </Suspense>
      )}

      {/* ── 화면 콘텐츠 래퍼: 뒤로가기 나가기 애니메이션 적용 대상 ── */}
      {/* GNB가 표시되는 화면은 has-gnb 클래스로 상단 여백 확보 */}
      {/* GNB(44px + safe-area)를 모든 화면에서 사용하므로 항상 has-gnb 적용 */}
      <div className={`screen-content has-gnb${isExiting ? ' screen-exit' : ''}`}>

      {screen === 'landing' && (
        <LandingScreen
          mode={mode}
          onModeChange={setMode}
          onNext={handleLandingNext}
          onFreewriteSave={handleFreewriteSave}
          loading={loading}
          error={error}
          showCalendarPrompt={showCalendarPrompt}
          onDismissCalendarPrompt={() => setShowCalendarPrompt(false)}
          onConnectCalendar={() => { setShowCalendarPrompt(false); handleOpenSettings(); }}
          quickQuestion={quickQuestion}
          onQuickQuestionSave={handleQuickQuestionSave}
          onQuickQuestionDismiss={() => setQuickQuestion(null)}
        />
      )}

      {screen === 'qa' && (
        <Suspense fallback={null}>
          <QAScreen
            key={sessionRounds.length}
            questions={currentQuestions}
            questionsLoading={questionsLoading}
            questionOffset={sessionRounds.length * 2}
            onContinue={handleContinue}
            onEnd={handleEnd}
            onRegenerate={handleRegenerateQuestion}
            onReport={(index, reason) => handleReportQuestion(index, currentQuestions, reason)}
            loading={loading}
            error={error}
            mode={mode}
          />
        </Suspense>
      )}

      {screen === 'session-view' && selectedSession && (
        // key로 세션 전환 시 슬라이드 인 애니메이션 재생, Suspense로 지연 로드
        <Suspense fallback={null}>
          <div key={`sv-${selectedSession.date}-${selectedSession.idx}`} className="screen-enter">
            <SessionView
              date={selectedSession.date}
              session={(sessions[selectedSession.date] || [])[selectedSession.idx]}
              sessions={sessions}
              onHome={handleHome}
              onDelete={handleDeleteSession}
              onKeywordClick={(kw) => handleKeywordClick(kw, { screen: 'session-view' })}
              onUpdate={handleUpdateSession}
              onSelectSession={handleSelectSession}
              onTogglePinned={handleTogglePinned}
              loading={loading}
            />
          </div>
        </Suspense>
      )}

      {screen === 'keywords' && (
        <Suspense fallback={null}>
          <div key={`kw-${keywordsMode}`} className="screen-enter">
            <KeywordsView
              sessions={sessions}
              mode={keywordsMode}
              onKeywordClick={(kw) => handleKeywordClick(kw, { screen: 'keywords', mode: keywordsMode })}
              onHome={handleHome}
            />
          </div>
        </Suspense>
      )}

      {screen === 'keyword-search' && (
        <Suspense fallback={null}>
          <div key={`kws-${selectedKeyword}`} className="screen-enter">
            <KeywordSearchView
              sessions={sessions}
              keyword={selectedKeyword}
              onSelectSession={handleSelectSession}
              onBack={handleBackFromKeywordSearch}
            />
          </div>
        </Suspense>
      )}

      {screen === 'search' && (
        <Suspense fallback={null}>
          <SearchView
            sessions={sessions}
            onSelectSession={handleSelectSession}
            onBack={handleHome}
          />
        </Suspense>
      )}

      {screen === 'chat' && user && (
        <Suspense fallback={null}>
          <ChatView
            onSendMessage={handleChatSend}
            onHome={handleHome}
            hasMemory={buildAllSessionContexts(sessions).length > 0}
            sessions={sessions}
            onResurfacingComplete={refreshSessions}
            onOpenSettings={handleOpenSettings}
          />
        </Suspense>
      )}

      {screen === 'calendar' && (
        <Suspense fallback={null}>
          <RecordsPage
            sessions={sessions}
            onHome={handleHome}
            onSelectSession={handleSelectSession}
            onOpenSearch={handleOpenSearch}
            onReportGuestBlock={handleReportGuestBlock}
          />
        </Suspense>
      )}

      {screen === 'settings' && (
        <Suspense fallback={null}>
          <SettingsView
            onHome={handleHome}
            onDeleteAll={handleDeleteAll}
            onLogout={handleLogout}
            onDeleteAccount={handleDeleteAccount}
            user={user}
            onLogin={() => setShowAuth(true)}
            googleCalendar={googleCalendar}
            isMaster={isMaster}
            onOpenAdmin={handleOpenAdmin}
            onOpenPushSettings={handleOpenPushSettings}
          />
        </Suspense>
      )}

      {screen === 'admin' && isMaster && (
        <Suspense fallback={null}>
          <AdminView onBack={goBack} />
        </Suspense>
      )}

      {screen === 'push-settings' && (
        <Suspense fallback={null}>
          <PushSettingsView onBack={goBackWithAnimation} />
        </Suspense>
      )}

      </div>{/* /screen-content */}

    </div>
  );
}
